/**
 * HomePage Component
 *
 * Main home page that displays the interactive historical map as the primary visual element.
 * Integrates MapView and YearNotification components.
 * Note: Timeline is rendered in AppShell, not here, to avoid duplicates.
 *
 * Requirements: 1.3 - THE MapView SHALL be the primary visual element on the home page
 */

import type React from 'react';
import { useState, useCallback, useEffect, useRef } from 'react';
import { MapView, MapErrorBoundary } from '../../map/MapView';
import { YearNotification } from '../../map/YearNotification';
import { useTimelineStore } from '../../../stores/timelineStore';
import styles from './HomePage.module.css';

/**
 * HomePage Component
 *
 * The main home page that renders the interactive historical map.
 * - MapView fills the available content area
 * - YearNotification displays at top center
 * - Timeline displays at bottom
 *
 * @returns HomePage React component
 */
export const HomePage: React.FC = () => {
  // Get state from stores
  const selectedYear = useTimelineStore((state) => state.selectedYear);
  
  // Track previous year to detect changes for notification
  const prevYearRef = useRef(selectedYear);

  // Year notification visibility state
  // Requirement 4.6: THE Year_Notification SHALL auto-hide after 6 seconds of inactivity
  const [isYearNotificationVisible, setIsYearNotificationVisible] = useState(false);

  /**
   * Show year notification when year changes.
   * Requirement 4.7: WHEN the year changes, THE Year_Notification SHALL animate into view
   */
  useEffect(() => {
    if (selectedYear !== prevYearRef.current) {
      prevYearRef.current = selectedYear;
      // eslint-disable-next-line react-hooks/set-state-in-effect -- showing notification when external store year changes; this syncs animation state with Zustand store
      setIsYearNotificationVisible(true);
    }
  }, [selectedYear]);

  /**
   * Handle year notification hide callback.
   * Requirement 4.6: THE Year_Notification SHALL auto-hide after 6 seconds
   */
  const handleYearNotificationHide = useCallback(() => {
    setIsYearNotificationVisible(false);
  }, []);

  return (
    <div className={styles['homePage']} data-testid="home-page">
      {/* Map View - Primary visual element */}
      {/* Requirement 1.3: THE MapView SHALL be the primary visual element on the home page */}
      <MapErrorBoundary>
        <MapView className={styles['mapView'] ?? ''} />
      </MapErrorBoundary>

      {/* Year Notification - Top center */}
      {/* Requirements 4.1-4.8: Year notification display */}
      <YearNotification
        year={selectedYear}
        isVisible={isYearNotificationVisible}
        onHide={handleYearNotificationHide}
        className={styles['yearNotification'] ?? ''}
      />
    </div>
  );
};

export default HomePage;
